import type { Locale, Timezone } from '@/__generated__/graphql';
import { DEFAULT_REGION, type Region } from '@fsd/shared/config/region';
import type { AppLocale } from '../locale-storage';
import type { RegionMappings } from '../region-mappings-context';

/** Lo que importa de `user.preferences.region` (`GetUserPreferences`). */
export interface UserRegionPreference {
  numberLocale?: Locale | null;
  dateLocale?: Locale | null;
  timezone?: Timezone | null;
  hour12?: boolean | null;
  /** El `month` de `Intl.DateTimeFormat` (`short`, `long`…); el backend lo manda como string. */
  dateMonthFormat?: string | null;
}

export interface ResolveRegionInput {
  /** El idioma activo de Lingui. */
  activeLocale: AppLocale;
  /** La región del usuario; `null`/`undefined` mientras la query no llegó. */
  userRegion: UserRegionPreference | null | undefined;
  /** `hour12` de la organización; `null`/`undefined` si no lo define o no se pidió. */
  organizationHour12: boolean | null | undefined;
  mappings: RegionMappings;
}

/**
 * Resuelve la región con la misma precedencia que `useFormatters` (paridad estricta):
 * - números y fechas: el locale de la preferencia o, sin él, el idioma activo, filtrado por
 *   `Intl.*.supportedLocalesOf([preferencia, idioma activo])[0]` como hace el legado al formatear: los
 *   consumidores de `useRegion()` reciben un locale ya utilizable;
 * - zona: la de la preferencia; sin región del usuario, la de `DEFAULT_REGION` (Santiago); con región
 *   pero sin zona, ninguna (la del navegador). Un enum que las tablas no conocen también da ninguna;
 * - `hour12`: el del usuario, si no el de la organización, si no ninguno (el del locale);
 * - mes: el `dateMonthFormat` del usuario; sin región, el de `DEFAULT_REGION` (`short`). Con región
 *   pero sin `dateMonthFormat` (el backend siempre lo manda) también `short`: el legado le pasaría
 *   `null` a `Intl` y fallaría al formatear.
 */
export function resolveRegion({
  activeLocale,
  userRegion,
  organizationHour12,
  mappings,
}: ResolveRegionInput): Region {
  const preferred = (preference: Locale | null | undefined) =>
    (preference ? mappings.localeToBcp47(preference) : undefined) ?? activeLocale;
  // Mismo filtro que `useFormatters`: números con `Intl.NumberFormat`, fechas con `Intl.DateTimeFormat`.
  const supported = (
    supportedLocalesOf: (locales: string[]) => string[],
    preference: Locale | null | undefined,
  ) => supportedLocalesOf([preferred(preference), activeLocale])[0] ?? activeLocale;
  const timeZone = userRegion
    ? userRegion.timezone
      ? mappings.timeZoneToIana(userRegion.timezone)
      : undefined
    : DEFAULT_REGION.timeZone;

  return {
    locale: activeLocale,
    numberLocale: supported(Intl.NumberFormat.supportedLocalesOf, userRegion?.numberLocale),
    dateLocale: supported(Intl.DateTimeFormat.supportedLocalesOf, userRegion?.dateLocale),
    timeZone,
    hour12: userRegion?.hour12 ?? organizationHour12 ?? undefined,
    // Como el legado, el string del backend se pasa tal cual a `Intl` (no se valida acá).
    dateMonthFormat:
      (userRegion?.dateMonthFormat as Intl.DateTimeFormatOptions['month'] | null | undefined) ??
      DEFAULT_REGION.dateMonthFormat,
  };
}
